<script setup lang="ts">
import { computed } from "vue";
import {
  useExpertWallet,
  useExpertWalletLevelProgress,
} from "@/app/composables/wallet";
import { formatCurrency } from "@/v2Projects/utils/display";
import { formatBps } from "./walletTransactionDisplay";
import WalletSummaryLevelCard from "./WalletSummaryLevelCard.vue";

const props = defineProps<{
  actingOrgId: number | null | undefined;
}>();

const actingOrgId = computed(() => props.actingOrgId);

const {
  availableBalanceCents,
  holdBalanceCents,
  totalGrossSalesCents,
  totalNetCents,
  currency,
  isLoading: isWalletLoading,
  error: walletError,
} = useExpertWallet({ actingOrgId });

const {
  currentLevel,
  nextLevel,
  lifetimeGrossCents,
  remainingGrossCents,
  currency: levelCurrency,
  isLoading: isLevelLoading,
  error: levelError,
} = useExpertWalletLevelProgress({ actingOrgId });

const hasWallet = computed(() => actingOrgId.value != null && !walletError.value);

const availableLabel = computed(() =>
  hasWallet.value ? formatCurrency(availableBalanceCents.value, currency.value) : "—",
);
const heldLabel = computed(() =>
  hasWallet.value ? formatCurrency(holdBalanceCents.value, currency.value) : "—",
);
const grossLabel = computed(() =>
  hasWallet.value ? formatCurrency(totalGrossSalesCents.value, currency.value) : "—",
);
const netLabel = computed(() =>
  hasWallet.value ? formatCurrency(totalNetCents.value, currency.value) : "—",
);

const secondaryStats = computed(() => [
  {
    key: "held",
    label: "On hold",
    value: heldLabel.value,
    tooltip:
      "Funds reserved from in-progress work. They move to your available balance when the project is completed or an admin releases them.",
  },
  {
    key: "gross",
    label: "Gross sales",
    value: grossLabel.value,
    tooltip:
      "Your lifetime gross sales across all completed work. This is what counts toward your expert level.",
  },
  {
    key: "net",
    label: "Net earnings",
    value: netLabel.value,
    tooltip: "Your lifetime earnings after platform commission and processing fees.",
  },
]);

const levelCurrencyValue = computed(() => levelCurrency.value ?? currency.value);

const currentTitle = computed(() => currentLevel.value?.public_title ?? "Getting started");

const commissionLabel = computed(() => {
  const bps = currentLevel.value?.commission_rate_bps;
  if (bps == null) return null;
  return formatBps(bps);
});

const nextCommissionLabel = computed(() => {
  const bps = nextLevel.value?.commission_rate_bps;
  if (bps == null) return null;
  return formatBps(bps);
});

const hasNextLevel = computed(() => Boolean(nextLevel.value));

const targetGrossCents = computed(() => {
  if (nextLevel.value) return nextLevel.value.auto_enroll_threshold_gross_cents;
  if (remainingGrossCents.value !== null) {
    return lifetimeGrossCents.value + remainingGrossCents.value;
  }
  return lifetimeGrossCents.value;
});

const percent = computed(() => {
  if (!hasNextLevel.value) return 100;
  const target = targetGrossCents.value;
  if (target <= 0) return 0;
  return Math.min(100, Math.max(0, (lifetimeGrossCents.value / target) * 100));
});

const percentLabel = computed(() => `${Math.round(percent.value)}%`);

const remainingLabel = computed(() => {
  if (!hasNextLevel.value || remainingGrossCents.value === null) return null;
  return formatCurrency(Math.max(0, remainingGrossCents.value), levelCurrencyValue.value);
});

const remainingToNextLevelLabel = computed(() => {
  if (!remainingLabel.value || !nextLevel.value) return null;
  return `${remainingLabel.value} more in gross sales to reach ${nextLevel.value.public_title}`;
});

const nextCommissionBenefitLabel = computed(() => {
  if (!nextCommissionLabel.value || !nextLevel.value) return null;
  return `Experts at ${nextLevel.value.public_title} pay ${nextCommissionLabel.value} commission`;
});

const progressCaption = computed(() => {
  if (!hasNextLevel.value) return "You've reached the highest expert level.";
  const earned = formatCurrency(lifetimeGrossCents.value, levelCurrencyValue.value);
  const target = formatCurrency(targetGrossCents.value, levelCurrencyValue.value);
  return `${earned} of ${target} in gross sales`;
});

const levelTooltip = computed(() => (
  currentLevel.value?.public_description ??
  "Your gross sales determine your expert level. Reach the next threshold to unlock a better commission rate."
));
</script>

<template>
  <WalletSummaryLevelCard
    :available-label="availableLabel"
    balance-caption="Ready to be paid out"
    :secondary-stats="secondaryStats"
    :is-wallet-loading="isWalletLoading"
    :current-level="currentLevel"
    :next-level="nextLevel"
    :current-title="currentTitle"
    :commission-label="commissionLabel"
    :is-level-loading="isLevelLoading"
    :has-level-error="Boolean(levelError)"
    :level-tooltip="levelTooltip"
    :percent="percent"
    :percent-label="percentLabel"
    :progress-caption="progressCaption"
    :remaining-to-next-level-label="remainingToNextLevelLabel"
    :next-commission-benefit-label="nextCommissionBenefitLabel"
  />
</template>
